import { type ApiFetchContext, apiFetch } from "../client";
import type { BackendLang } from "../headers";
import type { SupportEntry } from "../types/base";
import type {
  ContentAnnounceRawEnvelope,
  ContentMenu,
  ContentMenuRaw,
  ContentVo,
  ContentVoRaw,
} from "../types/content";

function normalize(raw: ContentVoRaw): ContentVo {
  return {
    id: raw.id,
    code: raw.code ?? "",
    title: raw.title ?? "",
    subtitle: raw.subtitle ?? "",
    description: raw.description ?? "",
    content: raw.content ?? "",
    imageUrl: raw.imageUrl ?? "",
    position: raw.position ?? "",
    url: raw.url ?? "",
    linkType: raw.linkType ?? "",
    linkUrl: raw.linkUrl ?? "",
    sort: raw.sort ?? 0,
    createTime: raw.createTime,
  };
}

function normalizeMenu(raw: ContentMenuRaw): ContentMenu {
  return {
    id: raw.id,
    name: raw.name ?? "",
    code: raw.code ?? "",
    position: raw.position ?? "",
    pid: raw.pid ?? "",
    sort: raw.sort ?? 0,
    url: raw.url ?? "",
  };
}

export async function getSupportList(ctx: ApiFetchContext): Promise<SupportEntry[]> {
  return apiFetch<SupportEntry[]>(ctx, "/base/support/list");
}

/** GET /content/announce — `result: { docs: ContentVoRaw[] }` (verified). */
export async function getContentAnnounce(ctx: ApiFetchContext): Promise<ContentVo[]> {
  const raw = await apiFetch<ContentAnnounceRawEnvelope>(ctx, "/content/announce");
  return (raw?.docs ?? raw?.announce ?? []).map(normalize);
}

/** GET /content/list?position&typeId&tagCode — filtered content list. */
export async function getContentList(
  ctx: ApiFetchContext,
  input: { position?: string; typeId?: string; tagCode?: string } = {},
): Promise<ContentVo[]> {
  const parts: string[] = [];
  if (input.position) parts.push(`position=${encodeURIComponent(input.position)}`);
  if (input.typeId) parts.push(`typeId=${encodeURIComponent(input.typeId)}`);
  if (input.tagCode) parts.push(`tagCode=${encodeURIComponent(input.tagCode)}`);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  const raw = await apiFetch<ContentVoRaw[]>(ctx, `/content/list${qs}`);
  return (raw ?? []).map(normalize);
}

/** GET /content/faq — flat FAQ list. */
export async function getContentFaq(ctx: ApiFetchContext): Promise<ContentVo[]> {
  const raw = await apiFetch<ContentVoRaw[]>(ctx, "/content/faq");
  return (raw ?? []).map(normalize);
}

/**
 * GET /content/detail?id|code — single article.
 *
 * Backend `getContentByCode(code, lang)` resolves by contentCode + lang
 * (isShow=Y), falling back to en_US when the requested lang has no row. Pass
 * `lang` so footer help articles render in the visitor's locale; omit it to
 * keep the backend default (ru_RU).
 */
export async function getContentDetail(
  ctx: ApiFetchContext,
  input: { id?: string; code?: string },
  opts?: { lang?: BackendLang },
): Promise<ContentVo> {
  const parts: string[] = [];
  if (input.id) parts.push(`id=${encodeURIComponent(input.id)}`);
  if (input.code) parts.push(`code=${encodeURIComponent(input.code)}`);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  const raw = await apiFetch<ContentVoRaw>(
    ctx,
    `/content/detail${qs}`,
    opts?.lang ? { lang: opts.lang } : {},
  );
  return normalize(raw);
}

/** GET /content/menu/list?position&pid — content nav menu tree slice. */
export async function getContentMenu(
  ctx: ApiFetchContext,
  input: { position?: string; pid?: string } = {},
): Promise<ContentMenu[]> {
  const parts: string[] = [];
  if (input.position) parts.push(`position=${encodeURIComponent(input.position)}`);
  if (input.pid) parts.push(`pid=${encodeURIComponent(input.pid)}`);
  const qs = parts.length > 0 ? `?${parts.join("&")}` : "";
  const raw = await apiFetch<ContentMenuRaw[]>(ctx, `/content/menu/list${qs}`);
  return (raw ?? []).map(normalizeMenu);
}
